import React, { FC, useMemo } from 'react';
import { useAuthContext } from '@/auth/hooks';
import { OrionAccess, OrionModule, OrionSubmoduleOptions } from '@/types';
import { RouterLink } from '@/routes/components';
import { LinkProps } from 'react-router-dom';
import { styled } from '@mui/material/styles';

const StyledRouterLink = styled(RouterLink)({
  color: '#2196F3',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
});

type LinkCheckPermissionProps = {
  module: OrionModule;
  submodule: OrionSubmoduleOptions;
  access: OrionAccess;
  children: React.ReactNode;
  alternativeAccess?: OrionAccess;
  alternativeRoute?: string;
  href?: string;
} & Omit<LinkProps, 'to'>;

export const LinkCheckPermission: FC<LinkCheckPermissionProps> = ({
  children,
  module,
  submodule,
  access,
  href,
  alternativeAccess,
  alternativeRoute,
  ...others
}) => {
  const { user } = useAuthContext();

  const userHasPermission = useMemo(
    () => user?.checkPermission(module, submodule, access),
    [user, module, submodule, access]
  );

  const userHasAlternativePermission = useMemo(
    () => alternativeAccess && user?.checkPermission(module, submodule, alternativeAccess),
    [user, module, submodule, alternativeAccess]
  );

  if (userHasPermission && href) {
    return (
      <StyledRouterLink href={href} {...others}>
        {children}
      </StyledRouterLink>
    );
  }

  if (!!alternativeAccess && !!alternativeRoute && userHasAlternativePermission) {
    const { color: _, ...otherProps } = others;
    return (
      <StyledRouterLink href={alternativeRoute} {...otherProps}>
        {children}
      </StyledRouterLink>
    );
  }

  return children;
};
